import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { onMounted, ref, watch, createApp, computed, App } from "vue";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Location } from "~/js/interfaces/locations";
import MarkerButton from "~/vue/atoms/markers/MarkerButton.vue";

interface Props {
	locations: Location[];
	utilities?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'RegionalMap',
  props: {
    locations: {},
    utilities: { default: "shadow-card" }
  },
  emits: ["filter"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const mapContainer = ref<HTMLElement | null>(null);
let map: mapboxgl.Map | null = null;

// Store references to clean up Vue apps properly
const markerInstances: { id: string; marker: mapboxgl.Marker; app: App }[] = [];

const activeId = ref<string | null>(null);
const mapboxApi = computed(
	() =>
		import.meta.env.VITE_MAPBOX_API ??
		"pk.eyJ1IjoicGF1ZGlnaXRhbCIsImEiOiJjbG5tem94dHgwMTd6MmtwMW9xcWwwcndyIn0.CxkLe0M6RMtKcfxx9Tj9kQ",
);

const reset = () => {
	activeId.value = null;
};

__expose({ reset });

onMounted(() => {
	if (!mapContainer.value) return;

	mapboxgl.accessToken = mapboxApi.value;

	map = new mapboxgl.Map({
		container: mapContainer.value,
		style: "mapbox://styles/mapbox/light-v11",
		center: [4.3517, 50.8503],
		zoom: 8,
		dragRotate: false,
		pitchWithRotate: false,
		cooperativeGestures: true,
	});

	map.on("load", () => {
		addMarkers();
		fitMapToMarkers();
	});
});

const fitMapToMarkers = () => {
	if (!map || props.locations.length === 0) return;

	const bounds = new mapboxgl.LngLatBounds();
	props.locations.forEach((loc) => bounds.extend([loc.longitude, loc.latitude]));

	map.fitBounds(bounds, { padding: 70, maxZoom: 14, animate: false });
};

const addMarkers = () => {
	if (!map) return;

	// Unmount Vue apps and remove markers to prevent memory leaks/warnings
	markerInstances.forEach((m) => {
		m.app.unmount();
		m.marker.remove();
	});
	markerInstances.length = 0;

	props.locations.forEach((loc) => {
		const el = document.createElement("div");

		const visualWrapper = document.createElement("div");
		visualWrapper.style.transition = "opacity 0.3s ease, transform 0.3s ease, filter 0.3s ease";
		el.appendChild(visualWrapper);

		const markerApp = createApp(MarkerButton, {
			type: loc.type,
			status: loc.availableNow ? "available" : "soon",
			title: loc.title,
		});

		markerApp.mount(visualWrapper);

		const marker = new mapboxgl.Marker({ element: el }).setLngLat([loc.longitude, loc.latitude]).addTo(map!);

		el.addEventListener("click", (e) => {
			e.stopPropagation();
			activeId.value = loc.id;
			emit("filter", loc.id);
		});

		// Save app instance for cleanup later
		markerInstances.push({ id: loc.id, marker, app: markerApp });
	});

	updateMarkerStyles();
};

const updateMarkerStyles = () => {
	markerInstances.forEach((item) => {
		const wrapper = item.marker.getElement().firstChild as HTMLElement;
		if (!wrapper) return;

		if (activeId.value === null) {
			wrapper.style.opacity = "1";
			wrapper.style.filter = "none";
			wrapper.style.transform = "scale(1)";
			wrapper.style.zIndex = "5";
		} else if (item.id === activeId.value) {
			wrapper.style.opacity = "1";
			wrapper.style.filter = "none";
			wrapper.style.transform = "scale(1.2)";
			wrapper.style.zIndex = "10";
		} else {
			wrapper.style.opacity = "0.8";
			wrapper.style.filter = "grayscale(100%)";
			wrapper.style.transform = "scale(0.8)";
			wrapper.style.zIndex = "1";
		}
	});
};

watch(activeId, () => updateMarkerStyles());

watch(
	() => props.locations,
	() => {
		if (map && map.loaded()) {
			activeId.value = null;
			addMarkers();
			fitMapToMarkers();
		}
	},
	{ deep: true },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["aspect-[3/2] overflow-hidden rounded-3xl", __props.utilities])
  }, [
    _createElementVNode("div", {
      ref_key: "mapContainer",
      ref: mapContainer,
      class: "h-full w-full grow"
    }, null, 512)
  ], 2))
}
}

})